import { useEffect, useRef, useState } from "react";
import { polygonCluster, polygonWsSymbol } from "../utils/polygonSymbol";

// Mirrors the Polygon FMV subscription that char.jsx uses for its live chart
// updates, but isolated so any component (e.g. the portfolio buy/sell sheet)
// can pull live prices without depending on /charts being mounted.
//
// Returns the latest FMV price as a number, or null until the first tick.
const useLivePrice = (ticker) => {
  const [price, setPrice] = useState(null);
  const wsRef = useRef(null);

  useEffect(() => {
    setPrice(null);

    const apiKey = process.env.REACT_APP_API_KEY;
    if (!apiKey || !ticker) return undefined;

    const wsPath = polygonCluster(ticker);

    // X:BTCUSD -> X:BTC-USD, C:EURUSD -> C:EUR-USD. The forex half was missing,
    // so this hook returned null forever for every currency pair.
    const formattedSymbol = polygonWsSymbol(ticker);

    const ws = new WebSocket(
      `wss://business.polygon.io/${wsPath}?apiKey=${apiKey}`
    );
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({ action: "auth", params: apiKey }));
      ws.send(
        JSON.stringify({ action: "subscribe", params: `FMV.${formattedSymbol}` })
      );
    };

    ws.onmessage = (event) => {
      try {
        const msgs = JSON.parse(event.data);
        if (!Array.isArray(msgs)) return;
        for (const msg of msgs) {
          if (
            msg.ev === "FMV" &&
            msg.sym === formattedSymbol &&
            typeof msg.fmv === "number" &&
            msg.fmv > 0
          ) {
            setPrice(msg.fmv);
          }
        }
      } catch {
        /* ignore malformed frames */
      }
    };

    return () => {
      try {
        if (
          ws.readyState === WebSocket.OPEN ||
          ws.readyState === WebSocket.CONNECTING
        ) {
          ws.close();
        }
      } catch {
        /* ignore close errors */
      }
      if (wsRef.current === ws) wsRef.current = null;
    };
  }, [ticker]);

  return price;
};

export default useLivePrice;
